import { useSelector, useDispatch } from 'react-redux'
import type {
  PastPerformancePlayerData,
  PerformanceMetricStat,
  HistoricalPerformanceToggleOptions,
} from '@pff-consumer/schema'
import { playerProfilesSelectors, playerProfileActions } from './player-profile.slice'

export const playerProfileHooks = {
  useGetSelectedPlayer: () => useSelector(playerProfilesSelectors.getSelectedPlayer),
  useGetSelectedPlayerId: () => useSelector(playerProfilesSelectors.getSelectedPlayerId),
  useGetSelectedPlayerPosition: () => useSelector(playerProfilesSelectors.getSelectedPlayerPosition),
  useGetSelectedPlayerTeam: () => useSelector(playerProfilesSelectors.getSelectedPlayerTeam),
  useGetSelectedGameWeek: () => useSelector(playerProfilesSelectors.getCurrentGameWeek),
  useGetCurrentSeason: () => useSelector(playerProfilesSelectors.getCurrentSeason),
  useGetPlayerPastPerformance: () => useSelector(playerProfilesSelectors.getPlayerPastPerformance),
  useGetPlayerHistoricalStatsSummary: (player: PastPerformancePlayerData) =>
    useSelector(playerProfilesSelectors.getPlayerHistoricalStatsSummary(player)),
  useGetHistoricalPerformanceSelectedStat: () =>
    useSelector(playerProfilesSelectors.getHistoricalPerformanceSelectedStat),
  useGetHistoricalPerformanceSelectedToggle: () =>
    useSelector(playerProfilesSelectors.getHistoricalPerformanceSelectedToggle),
  useGetHistoricalPerformanceSelectedStatName: () =>
    useSelector(playerProfilesSelectors.getHistoricalPerformanceSelectedStatName),
  useGetHistoricalPerformanceSelectedMetric: () =>
    useSelector(playerProfilesSelectors.getHistoricalPerformanceSelectedMetric),
  useGetHistoricalPerformanceBetLine: () => useSelector(playerProfilesSelectors.getHistoricalPerformanceBetLine),
  useGetFantasyRankings: () => useSelector(playerProfilesSelectors.getFantasyRankings),
  useGetIsInSeason: () => useSelector(playerProfilesSelectors.getIsInSeason),
  useGetInjuryStatus: () => useSelector(playerProfilesSelectors.getPlayerInjuryStatus),
  useGetActiveSeasons: () => useSelector(playerProfilesSelectors.getActiveSeasons),
  useGetIsPlayerActive: () => useSelector(playerProfilesSelectors.getIsPlayerActive),
  useGetPlayerTeamForLeague: () => useSelector(playerProfilesSelectors.getPlayerTeamForLeague),
  useGetIsMatchupsDataAvailable: () => useSelector(playerProfilesSelectors.getIsMatchupsDataAvailable),
  useGetIsMatchupsVisible: () => useSelector(playerProfilesSelectors.getIsMatchupsVisible),
  useGetMatchupsOverallScore: () => useSelector(playerProfilesSelectors.getMatchupsOverallScore),
  useGetGameScripts: () => useSelector(playerProfilesSelectors.getGameScripts),
  useGetNextGame: () => useSelector(playerProfilesSelectors.getNextGame),
  useGetIsByeWeek: () => useSelector(playerProfilesSelectors.getIsByeWeek),
  useGetDefaultActiveSeason: () => useSelector(playerProfilesSelectors.getDefaultActiveSeason),
  useGetIsFantasyInsightsDataAvailable: () => useSelector(playerProfilesSelectors.getIsFantasyInsightsDataAvailable),
  useGetIsHistoricalPerformanceApplicable: () =>
    useSelector(playerProfilesSelectors.getIsHistoricalPerformanceApplicable),
  useGetHasHistoricalPerformanceData: () => useSelector(playerProfilesSelectors.getHasHistoricalPerformanceData),
  useGetHeaderPremiumStatsUrl: () => useSelector(playerProfilesSelectors.getHeaderPremiumStatsUrl),
  usePlayerProfileActions: () => {
    const dispatch = useDispatch()
    return {
      updateHistoricalPerformanceSelectedStat: (payload: PerformanceMetricStat) => {
        dispatch(playerProfileActions.updateHistoricalPerformanceSelectedStat(payload))
      },
      updateHistoricalPerformanceSelectedToggle: (payload: HistoricalPerformanceToggleOptions) => {
        dispatch(playerProfileActions.updateHistoricalPerformanceSelectedToggle(payload))
      },
      updateLeague: (payload: 'nfl' | 'ncaa') => {
        dispatch(playerProfileActions.setLeague(payload))
      },
    }
  },
  useGetLatestNews: () => useSelector(playerProfilesSelectors.getLatestNews),
  useGetIsLatestNewsAvailable: () => useSelector(playerProfilesSelectors.getIsLatestNewsAvailable),
  useGetWeekProjections: () => useSelector(playerProfilesSelectors.getWeekProjections),
  useGetIsWeekProjectionsAvailable: () => useSelector(playerProfilesSelectors.getIsWeekProjectionsAvailable),
  useGetHasCareerData: () => useSelector(playerProfilesSelectors.getHasCareerData),
  usePageUpdatedAt: () => useSelector(playerProfilesSelectors.getPageUpdatedAt),
  useGetLeague: () => useSelector(playerProfilesSelectors.getLeague),
}
